import { useState } from 'react';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import { Copy, CheckCircle2, MessageCircle, Pill, AlertTriangle, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';

const LinkTab = ({ famiglia, medicines = [], onNavigateToMedicines }) => {
  const [copied, setCopied] = useState(false);
  const fullUrl = famiglia ? `${window.location.origin}/app/${famiglia.token}` : '';
  
  // Controlla se ci sono medicine configurate
  const hasMedicines = medicines && medicines.length > 0;

  const copyLink = () => {
    navigator.clipboard.writeText(fullUrl);
    setCopied(true);
    toast.success('Link copiato!');
    setTimeout(() => setCopied(false), 2000);
  };

  const shareWhatsApp = () => {
    const message = encodeURIComponent(
      `Ciao! Usa questo link per registrare le attività di ${famiglia?.nome_assistito}:\n${fullUrl}\n\nNon serve installare nulla!`
    );
    window.open(`https://wa.me/?text=${message}`, '_blank');
  };

  // Se non ci sono medicine, mostra messaggio per configurarle prima
  if (!hasMedicines) {
    return (
      <div className="space-y-6">
        <h2 className="text-xl font-bold text-[#2C3E50]">Link per l'Assistente</h2>
        
        <div className="bg-gradient-to-br from-[#FEF3C7] to-[#FEF9C3] rounded-2xl p-6 border border-[#FCD34D]">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-[#F59E0B] rounded-xl flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-[#92400E] text-lg mb-2">Prima configura le medicine!</h3>
              <p className="text-[#B45309] text-sm mb-4">
                Per condividere il link con l'assistente, devi prima configurare le medicine da somministrare a {famiglia?.nome_assistito}.
              </p>
              <p className="text-[#B45309] text-sm">
                L'assistente ha bisogno di sapere quali farmaci gestire. Senza medicine configurate, l'app non avrà nulla da mostrare.
              </p>
            </div>
          </div>
        </div>

        <Button
          onClick={onNavigateToMedicines}
          className="w-full h-14 bg-[#4A7C59] hover:bg-[#3A6345] text-white rounded-xl font-bold shadow-lg"
        >
          <Pill className="w-5 h-5 mr-2" />
          Configura Medicine
          <ArrowRight className="w-5 h-5 ml-2" />
        </Button>

        <div className="bg-[#FAF9F6] rounded-xl p-4 text-center">
          <p className="text-sm text-[#64748B]">
            Dopo aver configurato almeno una medicina, potrai generare e condividere il link con l'assistente.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-[#2C3E50]">Link per l'Assistente</h2>
      
      <div className="bg-gradient-to-br from-[#E8F5E9] to-[#F0F9F1] rounded-2xl p-6 border border-[#C8E6C9]">
        <div className="flex items-center gap-2 mb-3">
          <CheckCircle2 className="w-5 h-5 text-[#4A7C59]" />
          <p className="text-sm text-[#4A7C59] font-semibold">{medicines.length} medicine configurate</p>
        </div>

        <p className="text-sm text-[#64748B] mb-3">{famiglia?.nome_assistente || "L'assistente"} può inquadrare questo codice con la fotocamera del telefono:</p>
        <div className="flex justify-center mb-4" data-testid="link-tab-caregiver-qr">
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-2">
            <QRCodeSVG value={fullUrl} size={160} level="M" includeMargin />
          </div>
        </div>
        <p className="text-sm text-[#64748B] mb-3">Condividi questo link con {famiglia?.nome_assistente}:</p>
        
        <div className="flex items-center gap-2 bg-white rounded-xl border border-[#E5E7EB] p-2">
          <p className="flex-1 px-3 font-mono text-sm text-[#2C3E50] truncate">{fullUrl}</p>
          <Button
            onClick={copyLink}
            variant="ghost"
            className="rounded-lg hover:bg-[#E8F5E9]"
            data-testid="btn-copy-link"
          >
            {copied ? <CheckCircle2 className="w-5 h-5 text-[#4A7C59]" /> : <Copy className="w-5 h-5" />}
          </Button>
        </div>
      </div>

      <Button
        onClick={shareWhatsApp}
        className="w-full h-14 bg-[#25D366] hover:bg-[#1DA851] text-white rounded-xl font-bold shadow-lg"
        data-testid="btn-share-whatsapp"
      >
        <MessageCircle className="w-5 h-5 mr-2" />
        Invia via WhatsApp
      </Button>

      <div className="bg-[#FAF9F6] rounded-xl p-4 text-center">
        <p className="text-sm text-[#64748B]">
          L'assistente non deve installare nulla. Basta aprire il link dal telefono!
        </p>
      </div>
    </div>
  );
};

export default LinkTab;
